<template>
  <v-container fluid class="pa-4">
    <v-row no-gutters>
      <!-- Main Planner Content -->
      <v-col :cols="12" :lg="8" :xl="9">
        <div class="main-planner-content">
          <!-- Month Navigation -->
          <v-card class="mb-4" elevation="2">
            <v-card-text class="py-3">
              <v-row align="center">
                <v-col cols="auto">
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    :disabled="loading"
                    @click="previousMonth"
                  >
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                </v-col>

                <v-col cols="auto">
                  <v-menu
                    v-model="monthPickerOpen"
                    location="bottom"
                    :close-on-content-click="false"
                  >
                    <template #activator="{ props }">
                      <v-btn
                        variant="outlined"
                        v-bind="props"
                        :disabled="loading"
                      >
                        {{ monthYearLabel }}
                        <v-icon end>mdi-calendar</v-icon>
                      </v-btn>
                    </template>
                    <v-card width="300">
                      <v-card-text>
                        <v-row>
                          <v-col>
                            <v-select
                              v-model="selectedMonth"
                              :items="monthOptions"
                              label="Maand"
                              density="compact"
                              variant="outlined"
                              @update:model-value="changeMonthFromPicker"
                            />
                          </v-col>
                          <v-col>
                            <v-text-field
                              v-model.number="selectedYear"
                              label="Jaar"
                              type="number"
                              density="compact"
                              variant="outlined"
                              @keyup.enter="changeMonthFromPicker"
                              @blur="changeMonthFromPicker"
                            />
                          </v-col>
                        </v-row>
                        <v-card-actions>
                          <v-spacer />
                          <v-btn
                            variant="text"
                            @click="monthPickerOpen = false"
                          >
                            Sluiten
                          </v-btn>
                        </v-card-actions>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </v-col>

                <v-col cols="auto">
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    :disabled="loading"
                    @click="nextMonth"
                  >
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </v-col>

                <v-col cols="auto">
                  <v-btn
                    variant="outlined"
                    color="primary"
                    :disabled="loading"
                    @click="goToCurrentMonth"
                  >
                    Vandaag
                  </v-btn>
                </v-col>

                <v-col cols="auto">
                  <v-btn-toggle
                    v-model="viewMode"
                    color="primary"
                    variant="outlined"
                    density="compact"
                    mandatory
                    @update:model-value="switchView"
                  >
                    <v-btn value="month" size="small">
                      <v-icon start>mdi-calendar-month</v-icon>
                      Maand
                    </v-btn>
                    <v-btn value="week" size="small">
                      <v-icon start>mdi-calendar-week</v-icon>
                      Week
                    </v-btn>
                  </v-btn-toggle>
                </v-col>

                <v-spacer />

                <v-col cols="auto">
                  <v-btn
                    color="primary"
                    :disabled="loading"
                    @click="refreshData"
                  >
                    <v-icon start>mdi-refresh</v-icon>
                    Vernieuwen
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Filters -->
          <v-card class="mb-4" elevation="2">
            <v-card-text class="py-3">
              <v-row align="center">
                <v-col cols="12" md="6">
                  <v-select
                    v-model="selectedUsers"
                    :items="users"
                    item-title="displayName"
                    item-value="id"
                    label="Filter op medewerker"
                    multiple
                    clearable
                    density="compact"
                    variant="outlined"
                    @update:model-value="applyFilters"
                  >
                    <template #selection="{ item, index }">
                      <v-chip
                        v-if="index < 2"
                        size="small"
                        color="primary"
                        class="mr-1"
                      >
                        {{ item.raw.displayName }}
                      </v-chip>
                      <span v-if="index === 2" class="text-caption">
                        (+{{ selectedUsers.length - 2 }} meer)
                      </span>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="selectedDayReportTypes"
                    :items="dayReportTypes"
                    item-title="name"
                    item-value="id"
                    label="Filter op rapport type"
                    multiple
                    clearable
                    density="compact"
                    variant="outlined"
                    @update:model-value="applyFilters"
                  >
                    <template #selection="{ item, index }">
                      <v-chip
                        v-if="index < 2"
                        size="small"
                        color="secondary"
                        class="mr-1"
                      >
                        {{ item.raw.name }}
                      </v-chip>
                      <span v-if="index === 2" class="text-caption">
                        (+{{ selectedDayReportTypes.length - 2 }} meer)
                      </span>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Month View: Day Reports List -->
          <div v-if="viewMode === 'month'">
            <!-- Loading indicator when fetching fresh data but show existing data -->
            <v-alert
              v-if="loading && allDaysInMonth.length > 0"
              type="info"
              variant="tonal"
              class="mb-4"
              density="compact"
            >
              <div class="d-flex align-center">
                <v-progress-circular
                  indeterminate
                  color="info"
                  size="20"
                  class="mr-3"
                />
                <span>Vernieuwen van dag rapporten...</span>
              </div>
            </v-alert>

            <!-- Full loading screen only when no data exists -->
            <v-card v-else-if="loading" class="mb-4">
              <v-card-text class="text-center py-8">
                <v-progress-circular indeterminate color="primary" size="48" />
                <div class="mt-3">Dag rapporten laden...</div>
              </v-card-text>
            </v-card>

            <!-- Show data when available (either cached or freshly loaded) -->
            <div v-if="allDaysInMonth.length > 0">
              <v-card
                v-for="dayInfo in allDaysInMonth"
                :key="dayInfo.date"
                class="mb-4"
                elevation="2"
              >
                <!-- Date Header -->
                <v-card-title
                  :class="[
                    'py-3 d-flex align-center',
                    isToday(dayInfo.date)
                      ? 'today-header'
                      : dayInfo.isWeekend
                        ? 'bg-blue-grey-lighten-4'
                        : 'bg-grey-lighten-4',
                  ]"
                >
                  <v-icon
                    start
                    :color="
                      dayInfo.isWeekend ? 'blue-grey-darken-1' : 'primary'
                    "
                  >
                    mdi-calendar-today
                  </v-icon>
                  {{ formatDateHeader(dayInfo.date) }}
                  <v-chip
                    size="small"
                    :color="dayInfo.isWeekend ? 'blue-grey' : 'primary'"
                    class="ml-3"
                  >
                    {{ dayInfo.reports.length }} rapport{{
                      dayInfo.reports.length !== 1 ? "en" : ""
                    }}
                  </v-chip>

                  <v-spacer />

                  <!-- Quick Create Button for this date -->
                  <v-btn
                    size="small"
                    color="success"
                    variant="outlined"
                    :disabled="loading"
                    @click="createDayReportForDate(dayInfo.date)"
                  >
                    <v-icon start size="small">mdi-plus</v-icon>
                    Rapport toevoegen
                  </v-btn>
                </v-card-title>

                <!-- Day Reports for this date -->
                <v-card-text v-if="dayInfo.reports.length > 0" class="pa-0">
                  <v-list>
                    <v-list-item
                      v-for="(report, index) in dayInfo.reports"
                      :key="report.id"
                      :class="{
                        'border-b': index < dayInfo.reports.length - 1,
                      }"
                      class="py-3"
                    >
                      <template #prepend>
                        <v-avatar :color="getStatusColor(report)" size="36">
                          <v-icon color="white">
                            {{ getStatusIcon(report) }}
                          </v-icon>
                        </v-avatar>
                      </template>

                      <v-list-item-title class="d-flex align-center">
                        <span class="font-weight-medium mr-2">{{
                          report.day_report_type
                        }}</span>
                        <v-chip
                          size="x-small"
                          :color="getStatusChipColor(report)"
                          class="mr-2"
                        >
                          {{ report.day_report_status }}
                        </v-chip>
                        <v-chip size="x-small" color="grey" variant="outlined">
                          {{
                            report.user_name || report.user?.name || "Onbekend"
                          }}
                        </v-chip>
                      </v-list-item-title>

                      <v-list-item-subtitle class="mt-1">
                        <div class="d-flex align-center flex-wrap">
                          <span
                            v-if="report.start_time || report.end_time"
                            class="mr-3"
                          >
                            <v-icon size="small" class="mr-1"
                              >mdi-clock-outline</v-icon
                            >
                            {{ formatTimeRange(report) }}
                          </span>
                          <span v-if="report.duration" class="mr-3">
                            <v-icon size="small" class="mr-1"
                              >mdi-timer-outline</v-icon
                            >
                            {{ formatDurationHHMM(report.duration) }}
                          </span>
                          <span v-if="report.working !== undefined">
                            <v-icon size="small" class="mr-1">
                              {{
                                report.working ? "mdi-briefcase" : "mdi-home"
                              }}
                            </v-icon>
                            {{ report.working ? "Werkdag" : "Niet-werkdag" }}
                          </span>
                        </div>
                      </v-list-item-subtitle>

                      <template #append>
                        <div class="d-flex align-center">
                          <!-- View Detail Button (for opened/submitted working reports) -->
                          <v-btn
                            v-if="canViewDetail(report)"
                            icon
                            size="small"
                            variant="text"
                            color="primary"
                            @click="viewDayReportDetail(report)"
                          >
                            <v-icon>mdi-eye</v-icon>
                            <v-tooltip activator="parent" location="top">
                              Bekijk details
                            </v-tooltip>
                          </v-btn>

                          <!-- Edit Button -->
                          <v-btn
                            v-if="report.can_edit"
                            icon
                            size="small"
                            variant="text"
                            color="warning"
                            @click="editDayReport(report)"
                          >
                            <v-icon>mdi-pencil</v-icon>
                            <v-tooltip activator="parent" location="top">
                              Bewerk rapport
                            </v-tooltip>
                          </v-btn>

                          <!-- Delete Button -->
                          <v-btn
                            v-if="report.can_destroy"
                            icon
                            size="small"
                            variant="text"
                            color="error"
                            @click="deleteDayReport(report)"
                          >
                            <v-icon>mdi-delete</v-icon>
                            <v-tooltip activator="parent" location="top">
                              Verwijder rapport
                            </v-tooltip>
                          </v-btn>
                        </div>
                      </template>
                    </v-list-item>
                  </v-list>
                </v-card-text>

                <!-- Empty state for days with no reports -->
                <v-card-text v-else class="text-center py-2">
                  <v-icon color="grey-lighten-2" size="20" class="mr-2"
                    >mdi-calendar-blank-outline</v-icon
                  >
                  <span class="text-caption text-grey">
                    Geen rapporten voor deze dag
                  </span>
                </v-card-text>
              </v-card>
            </div>

            <!-- Empty state for month view when no data -->
            <v-card v-else elevation="2">
              <v-card-text class="text-center py-8">
                <v-icon size="64" color="grey-lighten-1"
                  >mdi-calendar-blank</v-icon
                >
                <div class="text-h6 mt-3 mb-2">Geen dag rapporten gevonden</div>
                <div class="text-body-2 text-grey">
                  Voor {{ monthYearLabel }} zijn geen dag rapporten gevonden die
                  voldoen aan de geselecteerde filters.
                </div>
                <v-btn color="primary" class="mt-4" @click="createDayReport">
                  <v-icon start>mdi-plus</v-icon>
                  Nieuw Rapport Aanmaken
                </v-btn>
              </v-card-text>
            </v-card>
          </div>

          <!-- Week View: 7-Column Layout -->
          <div v-else-if="viewMode === 'week'">
            <!-- Loading indicator for week view -->
            <v-alert
              v-if="loading && allDaysInWeek.length > 0"
              type="info"
              variant="tonal"
              class="mb-4"
              density="compact"
            >
              <div class="d-flex align-center">
                <v-progress-circular
                  indeterminate
                  color="info"
                  size="20"
                  class="mr-3"
                />
                <span>Vernieuwen van dag rapporten...</span>
              </div>
            </v-alert>

            <!-- Full loading screen only when no data exists -->
            <v-card v-else-if="loading" class="mb-4">
              <v-card-text class="text-center py-8">
                <v-progress-circular indeterminate color="primary" size="48" />
                <div class="mt-3">Dag rapporten laden...</div>
              </v-card-text>
            </v-card>

            <!-- Week data when available -->
            <div v-if="allDaysInWeek.length > 0">
              <v-card elevation="2">
                <v-card-text class="pa-0">
                  <v-row no-gutters class="week-view">
                    <v-col
                      v-for="dayInfo in allDaysInWeek"
                      :key="dayInfo.date"
                      cols="12"
                      sm="6"
                      md="4"
                      lg="auto"
                      xl="auto"
                      class="week-column"
                      :class="{
                        'weekend-column': dayInfo.isWeekend,
                      }"
                    >
                      <!-- Day Header -->
                      <div
                        class="day-header pa-3 text-center"
                        :class="[
                          isToday(dayInfo.date)
                            ? 'today-header'
                            : dayInfo.isWeekend
                              ? 'bg-blue-grey-lighten-4'
                              : 'bg-grey-lighten-4',
                        ]"
                      >
                        <div class="text-caption font-weight-medium">
                          {{ dayInfo.dayName }}
                        </div>
                        <div class="text-h6">
                          {{ dayInfo.dayNumber }} {{ dayInfo.monthName }}
                        </div>
                        <v-chip
                          size="x-small"
                          :color="dayInfo.isWeekend ? 'blue-grey' : 'primary'"
                          variant="tonal"
                        >
                          {{ dayInfo.reports.length }}
                        </v-chip>
                      </div>

                      <!-- Quick Add Button -->
                      <div class="pa-2 border-b">
                        <v-btn
                          size="small"
                          variant="outlined"
                          color="success"
                          block
                          :disabled="loading"
                          @click="createDayReportForDate(dayInfo.date)"
                        >
                          <v-icon start size="small">mdi-plus</v-icon>
                          Toevoegen
                        </v-btn>
                      </div>

                      <!-- Reports for this day -->
                      <div class="reports-column" style="min-height: 300px">
                        <div
                          v-if="dayInfo.reports.length === 0"
                          class="pa-4 text-center text-caption text-grey"
                        >
                          Geen rapporten
                        </div>

                        <v-card
                          v-for="report in dayInfo.reports"
                          :key="report.id"
                          class="ma-2"
                          variant="outlined"
                          density="compact"
                        >
                          <v-card-text class="pa-2">
                            <!-- Report Type -->
                            <div class="text-body-2 font-weight-medium mb-1">
                              {{ report.day_report_type }}
                            </div>

                            <!-- User -->
                            <v-chip
                              size="x-small"
                              color="grey"
                              variant="outlined"
                              class="mb-2"
                            >
                              {{
                                report.user_name ||
                                report.user?.name ||
                                "Onbekend"
                              }}
                            </v-chip>

                            <!-- Status -->
                            <v-chip
                              size="x-small"
                              :color="getStatusChipColor(report)"
                              class="mb-2 ml-1"
                            >
                              {{ report.day_report_status }}
                            </v-chip>

                            <!-- Time Info -->
                            <div
                              v-if="report.start_time || report.end_time"
                              class="text-caption text-grey mb-1"
                            >
                              <v-icon size="small" class="mr-1"
                                >mdi-clock-outline</v-icon
                              >
                              {{ formatTimeRange(report) }}
                            </div>

                            <!-- Duration -->
                            <div
                              v-if="report.duration"
                              class="text-caption text-grey mb-2"
                            >
                              <v-icon size="small" class="mr-1"
                                >mdi-timer-outline</v-icon
                              >
                              {{ formatDurationHHMM(report.duration) }}
                            </div>

                            <!-- Actions -->
                            <div class="d-flex justify-end mt-2">
                              <!-- View Detail Button -->
                              <v-btn
                                v-if="canViewDetail(report)"
                                icon
                                size="x-small"
                                variant="text"
                                color="primary"
                                @click="viewDayReportDetail(report)"
                              >
                                <v-icon size="small">mdi-eye</v-icon>
                              </v-btn>

                              <!-- Edit Button -->
                              <v-btn
                                v-if="report.can_edit"
                                icon
                                size="x-small"
                                variant="text"
                                color="warning"
                                @click="editDayReport(report)"
                              >
                                <v-icon size="small">mdi-pencil</v-icon>
                              </v-btn>

                              <!-- Delete Button -->
                              <v-btn
                                v-if="report.can_destroy"
                                icon
                                size="x-small"
                                variant="text"
                                color="error"
                                @click="deleteDayReport(report)"
                              >
                                <v-icon size="small">mdi-delete</v-icon>
                              </v-btn>
                            </div>
                          </v-card-text>
                        </v-card>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </div>

            <!-- Empty state for week view when no data -->
            <v-card v-else elevation="2">
              <v-card-text class="text-center py-8">
                <v-icon size="64" color="grey-lighten-1"
                  >mdi-calendar-blank</v-icon
                >
                <div class="text-h6 mt-3 mb-2">Geen dag rapporten gevonden</div>
                <div class="text-body-2 text-grey">
                  Voor {{ monthYearLabel }} zijn geen dag rapporten gevonden die
                  voldoen aan de geselecteerde filters.
                </div>
                <v-btn color="primary" class="mt-4" @click="createDayReport">
                  <v-icon start>mdi-plus</v-icon>
                  Nieuw Rapport Aanmaken
                </v-btn>
              </v-card-text>
            </v-card>
          </div>

          <!-- General Empty State (fallback) -->
          <v-card v-else elevation="2">
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="grey-lighten-1"
                >mdi-calendar-blank</v-icon
              >
              <div class="text-h6 mt-3 mb-2">Geen dag rapporten gevonden</div>
              <div class="text-body-2 text-grey">
                Voor {{ monthYearLabel }} zijn geen dag rapporten gevonden die
                voldoen aan de geselecteerde filters.
              </div>
              <v-btn color="primary" class="mt-4" @click="createDayReport">
                <v-icon start>mdi-plus</v-icon>
                Nieuw Rapport Aanmaken
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Error State -->
          <v-card v-if="error" color="error" variant="tonal" class="mt-4">
            <v-card-text class="d-flex align-center">
              <v-icon start>mdi-alert-circle</v-icon>
              {{ error }}
              <v-spacer />
              <v-btn variant="text" @click="refreshData">
                Opnieuw proberen
              </v-btn>
            </v-card-text>
          </v-card>
        </div>
      </v-col>

      <!-- Hours Summary Sidebar -->
      <v-col :cols="12" :lg="4" :xl="3" class="pl-lg-4">
        <div class="hours-summary-sticky">
          <!-- Hours Summary Panel -->
          <v-card class="hours-summary-card" elevation="4">
            <v-card-title class="py-3 d-flex align-center">
              <v-icon start color="primary">mdi-clock-outline</v-icon>
              Geplande uren
              {{
                viewMode === "week" ? `week ${selectedWeek}` : monthYearLabel
              }}
              <v-spacer />
              <v-btn
                icon
                size="small"
                variant="text"
                @click="toggleHoursSummary"
              >
                <v-icon>{{
                  showHoursSummary ? "mdi-eye-off" : "mdi-eye"
                }}</v-icon>
              </v-btn>
            </v-card-title>

            <v-divider />

            <v-card-text v-if="showHoursSummary" class="pa-0">
              <!-- Header Row -->
              <div class="pa-2 bg-grey-lighten-4 text-caption font-weight-bold">
                <v-row no-gutters align="center">
                  <v-col cols="5">Naam</v-col>
                  <v-col cols="2" class="text-center text-success">Werk</v-col>
                  <v-col cols="2" class="text-center text-info">Vrij</v-col>
                  <v-col cols="2" class="text-center">Totaal</v-col>
                  <v-col cols="1"></v-col>
                </v-row>
              </div>

              <!-- Users with hours -->
              <v-list density="compact" class="pa-0">
                <template
                  v-for="userSummary in usersWithHours"
                  :key="userSummary.userId"
                >
                  <v-list-item class="px-2 py-1">
                    <v-row no-gutters align="center">
                      <v-col cols="5">
                        <span class="text-body-2 font-weight-medium">{{
                          userSummary.userName
                        }}</span>
                      </v-col>
                      <v-col cols="2" class="text-center">
                        <span class="text-caption text-success"
                          >{{ userSummary.workingHours }}u</span
                        >
                      </v-col>
                      <v-col cols="2" class="text-center">
                        <span class="text-caption text-info"
                          >{{ userSummary.nonWorkingHours }}u</span
                        >
                      </v-col>
                      <v-col cols="2" class="text-center">
                        <span class="text-caption font-weight-bold"
                          >{{ userSummary.totalHours }}u</span
                        >
                      </v-col>
                      <v-col cols="1" class="text-center">
                        <v-btn
                          icon
                          size="x-small"
                          variant="text"
                          @click="toggleUserExpansion(userSummary.userId)"
                        >
                          <v-icon size="small">
                            {{
                              userSummary.expanded ? "mdi-minus" : "mdi-plus"
                            }}
                          </v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <!-- Expanded details -->
                  <v-expand-transition>
                    <div v-if="userSummary.expanded">
                      <v-list density="compact" class="ml-3 bg-grey-lighten-5">
                        <v-list-item
                          v-for="typeDetail in userSummary.details"
                          :key="typeDetail.typeName"
                          class="px-3 py-1"
                        >
                          <v-row no-gutters align="center">
                            <v-col cols="7">
                              <span class="text-caption">{{
                                typeDetail.typeName
                              }}</span>
                            </v-col>
                            <v-col cols="3" class="text-center">
                              <v-chip
                                size="x-small"
                                :color="
                                  typeDetail.isWorking ? 'success' : 'info'
                                "
                                variant="tonal"
                              >
                                {{ typeDetail.isWorking ? "werk" : "vrij" }}
                              </v-chip>
                            </v-col>
                            <v-col cols="2" class="text-center">
                              <span class="text-caption"
                                >{{ typeDetail.hours }}u</span
                              >
                            </v-col>
                          </v-row>
                        </v-list-item>
                      </v-list>
                    </div>
                  </v-expand-transition>
                </template>

                <!-- Users without hours (greyed out and compact) -->
                <template v-if="usersWithoutHours.length > 0">
                  <v-divider class="my-1" />
                  <div class="px-2 py-1">
                    <div class="text-caption text-grey font-weight-medium mb-1">
                      Geen uren:
                    </div>
                    <div class="d-flex flex-wrap ga-1">
                      <v-chip
                        v-for="userSummary in usersWithoutHours"
                        :key="userSummary.userId"
                        size="x-small"
                        variant="outlined"
                        color="grey"
                        class="text-caption"
                      >
                        {{ userSummary.userName }}
                      </v-chip>
                    </div>
                  </div>
                </template>

                <!-- Empty state -->
                <v-list-item
                  v-if="
                    usersWithHours.length === 0 &&
                    usersWithoutHours.length === 0
                  "
                  class="text-center py-4"
                >
                  <v-list-item-title class="text-grey">
                    <v-icon class="mb-2">mdi-calendar-blank</v-icon>
                    <div class="text-caption">Geen geplande uren</div>
                  </v-list-item-title>
                </v-list-item>
              </v-list>

              <!-- Summary totals -->
              <v-divider />
              <div class="pa-2 bg-primary-lighten-5">
                <v-row no-gutters align="center">
                  <v-col cols="5">
                    <span class="text-body-2 font-weight-bold">Totaal:</span>
                  </v-col>
                  <v-col cols="2" class="text-center">
                    <span class="text-caption font-weight-bold text-success"
                      >{{ totalSummary.workingHours }}u</span
                    >
                  </v-col>
                  <v-col cols="2" class="text-center">
                    <span class="text-caption font-weight-bold text-info"
                      >{{ totalSummary.nonWorkingHours }}u</span
                    >
                  </v-col>
                  <v-col cols="2" class="text-center">
                    <span class="text-caption font-weight-bold text-primary"
                      >{{ totalSummary.totalHours }}u</span
                    >
                  </v-col>
                  <v-col cols="1"></v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useAuthStoreRefactor } from "../../stores/authStoreRefactor";
import { useUsersStoreRefactor } from "../../stores/usersStoreRefactor";
import { useDayReportsStoreRefactor } from "../../stores/dayReportsStoreRefactor";
import { useDayReportTypesStoreRefactor } from "../../stores/dayReportTypesStoreRefactor";
import { useFormsStore } from "../../stores/formsStore";
import {
  formatDurationHHMM,
  secondsToHours,
} from "../../composables/useDurationFormatter";
import { useRouter, useRoute } from "vue-router";

export default {
  name: "PlannerView",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const authStore = useAuthStoreRefactor();
    const usersStore = useUsersStoreRefactor();
    const dayReportsStore = useDayReportsStoreRefactor();
    const dayReportTypesStore = useDayReportTypesStoreRefactor();
    const formsStore = useFormsStore();

    // Debug logging for production troubleshooting
    console.log("Planner stores initialization:", {
      authStore: !!authStore,
      usersStore: !!usersStore,
      dayReportsStore: !!dayReportsStore,
      dayReportTypesStore: !!dayReportTypesStore,
      formsStore: !!formsStore,
      dayReportsStoreType: typeof dayReportsStore,
      hasGetMonthlyReports: !!(
        dayReportsStore &&
        typeof dayReportsStore.getMonthlyReports === "function"
      ),
    });

    // Week number utility functions
    const getWeekNumber = (date) => {
      const d = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
      );
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    };

    const getDateFromWeek = (year, week) => {
      const simple = new Date(year, 0, 1 + (week - 1) * 7);
      const dow = simple.getDay();
      const ISOweekStart = simple;
      if (dow <= 4) {
        ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
      } else {
        ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
      }
      return ISOweekStart;
    };

    // Initialize from URL params or current date
    const initializeFromURL = () => {
      const now = new Date();

      if (route.query.view === "week" && route.query.week && route.query.year) {
        // Week mode initialization
        const urlWeek = Number(route.query.week);
        const urlYear = Number(route.query.year);

        if (
          urlWeek >= 1 &&
          urlWeek <= 53 &&
          urlYear >= 2000 &&
          urlYear <= 2100
        ) {
          const weekStart = getDateFromWeek(urlYear, urlWeek);
          return {
            month: weekStart.getMonth() + 1,
            year: weekStart.getFullYear(),
            week: urlWeek,
            weekYear: urlYear,
          };
        }
      }

      // Month mode initialization or fallback
      const urlMonth = route.query.month
        ? Number(route.query.month)
        : now.getMonth() + 1;
      const urlYear = route.query.year
        ? Number(route.query.year)
        : now.getFullYear();
      const currentWeek = getWeekNumber(now);

      return {
        month: urlMonth >= 1 && urlMonth <= 12 ? urlMonth : now.getMonth() + 1,
        year: urlYear >= 2000 && urlYear <= 2100 ? urlYear : now.getFullYear(),
        week: currentWeek,
        weekYear: now.getFullYear(),
      };
    };

    const {
      month: initialMonth,
      year: initialYear,
      week: initialWeek,
      weekYear: initialWeekYear,
    } = initializeFromURL();

    // Reactive data
    const selectedMonth = ref(initialMonth);
    const selectedYear = ref(initialYear);
    const selectedWeek = ref(initialWeek);
    const selectedWeekYear = ref(initialWeekYear);
    const selectedUsers = ref([]);
    const selectedDayReportTypes = ref([]);
    const monthPickerOpen = ref(false);
    const viewMode = ref(route.query.view === "week" ? "week" : "month"); // 'month' or 'week'
    const showHoursSummary = ref(true);
    const expandedUsers = ref(new Set());

    // Constants for month selector
    const monthOptions = [
      { title: "Januari", value: 1 },
      { title: "Februari", value: 2 },
      { title: "Maart", value: 3 },
      { title: "April", value: 4 },
      { title: "Mei", value: 5 },
      { title: "Juni", value: 6 },
      { title: "Juli", value: 7 },
      { title: "Augustus", value: 8 },
      { title: "September", value: 9 },
      { title: "Oktober", value: 10 },
      { title: "November", value: 11 },
      { title: "December", value: 12 },
    ];

    // Computed properties - only show loading for monthly data fetching, not for individual CRUD operations
    const loading = computed(() => {
      // Only show loading during getMonthlyReports calls, not during individual day report CRUD
      return dayReportsStore?.loadingMonthly || false;
    });
    const error = computed(() => dayReportsStore?.error || null);
    const currentUser = computed(() => authStore?.user || null);

    const users = computed(() => {
      const rawUsers = usersStore?.activeUsersInCurrentTenant || [];

      // Transform users to include full name and inactive status
      const transformedUsers = rawUsers.map((user) => ({
        ...user,
        displayName: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        sortName: user.first_name || "",
      }));

      // Sort alphabetically by first name
      return transformedUsers.sort((a, b) =>
        (a.sortName || "").localeCompare(b.sortName || "", "nl-NL"),
      );
    });

    const dayReportTypes = computed(() => {
      return (dayReportTypesStore?.dayReportTypes || [])
        .filter((type) => type?.active)
        .sort((a, b) => (a?.position || 0) - (b?.position || 0));
    });

    const monthYearLabel = computed(() => {
      if (viewMode.value === "week") {
        // Show week number and date range
        const startOfWeek = getDateFromWeek(
          selectedWeekYear.value,
          selectedWeek.value,
        );
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);

        const formatOptions = { day: "numeric", month: "short" };
        const startStr = startOfWeek.toLocaleDateString("nl-NL", formatOptions);
        const endStr = endOfWeek.toLocaleDateString("nl-NL", formatOptions);

        if (startOfWeek.getFullYear() === endOfWeek.getFullYear()) {
          return `Week ${selectedWeek.value} (${startStr} - ${endStr} ${startOfWeek.getFullYear()})`;
        } else {
          return `Week ${selectedWeek.value} (${startStr} ${startOfWeek.getFullYear()} - ${endStr} ${endOfWeek.getFullYear()})`;
        }
      } else {
        const month = monthOptions.find((m) => m.value === selectedMonth.value);
        return `${month?.title || "Unknown"} ${selectedYear.value}`;
      }
    });

    const filteredReports = computed(() => {
      let reports = dayReportsStore?.monthlyReports || [];

      // Filter by users
      if (selectedUsers.value?.length > 0) {
        reports = reports.filter((report) =>
          selectedUsers.value.includes(report?.user_id),
        );
      }

      // Filter by day report types
      if (selectedDayReportTypes.value?.length > 0) {
        reports = reports.filter((report) =>
          selectedDayReportTypes.value.includes(report?.day_report_type_id),
        );
      }

      return reports;
    });

    // Generate all days in the current month with their reports and weekend info
    const allDaysInMonth = computed(() => {
      const year = selectedYear.value;
      const month = selectedMonth.value;
      const daysInMonth = new Date(year, month, 0).getDate();
      const allDays = [];

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        // Use local date formatting to avoid timezone issues
        const dateString = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        // Get filtered reports for this date
        const reportsForDate = (filteredReports.value || [])
          .filter((report) => report?.date === dateString)
          .sort((a, b) => {
            // Sort by start_time first, then by type
            if (a?.start_time && b?.start_time) {
              return a.start_time.localeCompare(b.start_time);
            }
            if (a?.start_time && !b?.start_time) return -1;
            if (!a?.start_time && b?.start_time) return 1;
            return (a?.day_report_type || "").localeCompare(
              b?.day_report_type || "",
            );
          });

        allDays.push({
          date: dateString,
          reports: reportsForDate,
          isWeekend: isWeekend,
        });
      }

      return allDays;
    });

    // Generate week data for week view (7 days starting from Monday)
    const allDaysInWeek = computed(() => {
      // Use the selected week and year to get the exact week
      const startOfWeek = getDateFromWeek(
        selectedWeekYear.value,
        selectedWeek.value,
      );

      const weekDays = [];

      for (let i = 0; i < 7; i++) {
        const currentDay = new Date(startOfWeek);
        currentDay.setDate(startOfWeek.getDate() + i);

        const dateString = `${currentDay.getFullYear()}-${String(currentDay.getMonth() + 1).padStart(2, "0")}-${String(currentDay.getDate()).padStart(2, "0")}`;
        const dayOfWeek = currentDay.getDay(); // 0 = Sunday, 6 = Saturday
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        // Get filtered reports for this date
        const reportsForDate = (filteredReports.value || [])
          .filter((report) => report?.date === dateString)
          .sort((a, b) => {
            // Sort by start_time first, then by type
            if (a?.start_time && b?.start_time) {
              return a.start_time.localeCompare(b.start_time);
            }
            if (a?.start_time && !b?.start_time) return -1;
            if (!a?.start_time && b?.start_time) return 1;
            return (a?.day_report_type || "").localeCompare(
              b?.day_report_type || "",
            );
          });

        weekDays.push({
          date: dateString,
          dayName: currentDay.toLocaleDateString("nl-NL", { weekday: "long" }),
          dayNumber: currentDay.getDate(),
          monthName: currentDay.toLocaleDateString("nl-NL", { month: "short" }),
          isCurrentMonth: currentDay.getMonth() === selectedMonth.value - 1,
          reports: reportsForDate,
          isWeekend: isWeekend,
        });
      }

      return weekDays;
    });

    // Utility to check if a date is today
    const isToday = (dateString) => {
      const today = new Date();
      const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      return dateString === todayString;
    };

    // Parse duration in seconds to hours (e.g., 28800 -> 8, 5400 -> 1.5)
    const parseDurationToHours = (duration) => {
      return secondsToHours(duration || 0);
    };

    // Hours summary computed properties
    const hoursSummaryData = computed(() => {
      const userSummaries = {};

      // Get reports based on view mode
      let reportsToProcess = dayReportsStore?.monthlyReports || [];

      if (viewMode.value === "week") {
        // Filter to only include reports within the current week
        const weekStart = getDateFromWeek(
          selectedWeekYear.value,
          selectedWeek.value,
        );
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        const weekStartString = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, "0")}-${String(weekStart.getDate()).padStart(2, "0")}`;
        const weekEndString = `${weekEnd.getFullYear()}-${String(weekEnd.getMonth() + 1).padStart(2, "0")}-${String(weekEnd.getDate()).padStart(2, "0")}`;

        reportsToProcess = reportsToProcess.filter(
          (report) =>
            report.date >= weekStartString && report.date <= weekEndString,
        );
      }

      // Process reports (filtered by week if in week view, all monthly reports if in month view)
      reportsToProcess.forEach((report) => {
        const userId = report.user_id;
        const userName = report.user_name || report.user?.name || "Onbekend";
        const hours = parseDurationToHours(report.duration);
        const isWorking = report.working === true;
        const typeName = report.day_report_type || "Onbekend";

        if (!userSummaries[userId]) {
          userSummaries[userId] = {
            userId,
            userName,
            workingHours: 0,
            nonWorkingHours: 0,
            totalHours: 0,
            details: {},
            expanded: expandedUsers.value.has(userId),
          };
        }

        // Add to user totals
        if (isWorking) {
          userSummaries[userId].workingHours += hours;
        } else {
          userSummaries[userId].nonWorkingHours += hours;
        }
        userSummaries[userId].totalHours += hours;

        // Add to type details
        if (!userSummaries[userId].details[typeName]) {
          userSummaries[userId].details[typeName] = {
            typeName,
            hours: 0,
            isWorking,
          };
        }
        userSummaries[userId].details[typeName].hours += hours;
      });

      // Convert details objects to arrays and round all hours
      Object.values(userSummaries).forEach((summary) => {
        summary.workingHours = Math.round(summary.workingHours * 10) / 10;
        summary.nonWorkingHours = Math.round(summary.nonWorkingHours * 10) / 10;
        summary.totalHours = Math.round(summary.totalHours * 10) / 10;
        summary.details = Object.values(summary.details)
          .map((detail) => ({
            ...detail,
            hours: Math.round(detail.hours * 10) / 10,
          }))
          .sort((a, b) => a.typeName.localeCompare(b.typeName));
      });

      return Object.values(userSummaries);
    });

    const usersWithHours = computed(() => {
      return hoursSummaryData.value
        .filter((summary) => summary.totalHours > 0)
        .sort((a, b) => b.totalHours - a.totalHours); // Sort by total hours descending
    });

    const usersWithoutHours = computed(() => {
      // Get all users and find those not in the hoursSummaryData or with 0 hours
      const usersWithHoursIds = new Set(
        hoursSummaryData.value.map((summary) => summary.userId),
      );
      const allUsers = users.value || [];

      return allUsers
        .filter((user) => !usersWithHoursIds.has(user.id))
        .map((user) => ({
          userId: user.id,
          userName: user.first_name || user.name || "Onbekend",
          workingHours: 0,
          nonWorkingHours: 0,
          totalHours: 0,
          details: [],
          expanded: false,
        }))
        .sort((a, b) => a.userName.localeCompare(b.userName));
    });

    const totalSummary = computed(() => {
      const totalWorking = usersWithHours.value.reduce(
        (sum, user) => sum + user.workingHours,
        0,
      );
      const totalNonWorking = usersWithHours.value.reduce(
        (sum, user) => sum + user.nonWorkingHours,
        0,
      );

      return {
        workingHours: Math.round(totalWorking * 10) / 10,
        nonWorkingHours: Math.round(totalNonWorking * 10) / 10,
        totalHours: Math.round((totalWorking + totalNonWorking) * 10) / 10,
      };
    });

    // Methods
    const getReportsForDate = (date) => {
      return (filteredReports.value || [])
        .filter((report) => report?.date === date)
        .sort((a, b) => {
          // Sort by start_time first, then by type
          if (a?.start_time && b?.start_time) {
            return a.start_time.localeCompare(b.start_time);
          }
          if (a?.start_time && !b?.start_time) return -1;
          if (!a?.start_time && b?.start_time) return 1;
          return (a?.day_report_type || "").localeCompare(
            b?.day_report_type || "",
          );
        });
    };

    const formatDateHeader = (dateString) => {
      const date = new Date(dateString);
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      return date.toLocaleDateString("nl-NL", options);
    };

    const formatTimeRange = (report) => {
      if (!report) return "";

      if (report.start_time && report.end_time) {
        return `${report.start_time} - ${report.end_time}`;
      } else if (report.start_time) {
        return `vanaf ${report.start_time}`;
      } else if (report.end_time) {
        return `tot ${report.end_time}`;
      }
      return "";
    };

    const getStatusColor = (report) => {
      if (!report) return "grey";

      switch (report.day_report_status) {
        case "submitted":
          return "success";
        case "opened":
          return "warning";
        case "planned":
          return "info";
        case "closed":
          return "success";
        default:
          return "grey";
      }
    };

    const getStatusIconColor = () => {
      return "white";
    };

    const getStatusIcon = (report) => {
      if (!report) return "mdi-help-circle";

      switch (report.day_report_status) {
        case "submitted":
          return "mdi-check-circle";
        case "opened":
          return "mdi-folder-open";
        case "planned":
          return "mdi-calendar-clock";
        case "closed":
          return "mdi-lock";
        default:
          return "mdi-help-circle";
      }
    };

    const getStatusChipColor = (report) => {
      if (!report) return "grey";

      switch (report.day_report_status) {
        case "submitted":
          return "success";
        case "opened":
          return "warning";
        case "planned":
          return "info";
        case "closed":
          return "success";
        default:
          return "grey";
      }
    };

    const canViewDetail = (report) => {
      if (!report) return false;
      return (
        report.working &&
        (report.day_report_status === "opened" ||
          report.day_report_status === "submitted")
      );
    };

    // Update URL when month/year or week/year changes
    const updateURL = (month, year, week, weekYear) => {
      let newQuery = { ...route.query };

      if (viewMode.value === "week") {
        newQuery = { ...newQuery, week, year: weekYear, view: "week" };
        delete newQuery.month;
      } else {
        newQuery = { ...newQuery, month, year, view: "month" };
        delete newQuery.week;
      }

      // Only update URL if the values have actually changed
      const hasChanged =
        viewMode.value === "week"
          ? route.query.week != week || route.query.year != weekYear
          : route.query.month != month || route.query.year != year;

      if (hasChanged) {
        router.replace({ query: newQuery }).catch((error) => {
          console.debug("Navigation error (ignored):", error);
        });
      }
    };

    const previousMonth = () => {
      if (viewMode.value === "week") {
        // Navigate to previous week
        let newWeek = selectedWeek.value - 1;
        let newYear = selectedWeekYear.value;

        if (newWeek < 1) {
          newYear--;
          // Get the last week of the previous year
          // The last week of a year is the week that contains the last Thursday of that year
          const lastThursdayOfPrevYear = new Date(newYear, 11, 31);
          while (lastThursdayOfPrevYear.getDay() !== 4) {
            // 4 = Thursday
            lastThursdayOfPrevYear.setDate(
              lastThursdayOfPrevYear.getDate() - 1,
            );
          }
          newWeek = getWeekNumber(lastThursdayOfPrevYear);
        }

        selectedWeek.value = newWeek;
        selectedWeekYear.value = newYear;

        // Update month/year for data fetching
        const weekStart = getDateFromWeek(newYear, newWeek);
        selectedMonth.value = weekStart.getMonth() + 1;
        selectedYear.value = weekStart.getFullYear();
      } else {
        // Navigate to previous month
        if (selectedMonth.value === 1) {
          selectedMonth.value = 12;
          selectedYear.value--;
        } else {
          selectedMonth.value--;
        }
      }
      updateURL(
        selectedMonth.value,
        selectedYear.value,
        selectedWeek.value,
        selectedWeekYear.value,
      );
      loadWeeklyData();
    };

    const nextMonth = () => {
      if (viewMode.value === "week") {
        // Navigate to next week
        let newWeek = selectedWeek.value + 1;
        let newYear = selectedWeekYear.value;

        // Find the actual last week of the current year
        // The last week of a year is the week that contains the last Thursday of that year
        const lastThursdayOfYear = new Date(newYear, 11, 31);
        while (lastThursdayOfYear.getDay() !== 4) {
          // 4 = Thursday
          lastThursdayOfYear.setDate(lastThursdayOfYear.getDate() - 1);
        }
        const maxWeek = getWeekNumber(lastThursdayOfYear);

        if (newWeek > maxWeek) {
          newYear++;
          newWeek = 1;
        }

        selectedWeek.value = newWeek;
        selectedWeekYear.value = newYear;

        // Update month/year for data fetching
        const weekStart = getDateFromWeek(newYear, newWeek);
        selectedMonth.value = weekStart.getMonth() + 1;
        selectedYear.value = weekStart.getFullYear();
      } else {
        // Navigate to next month
        if (selectedMonth.value === 12) {
          selectedMonth.value = 1;
          selectedYear.value++;
        } else {
          selectedMonth.value++;
        }
      }
      updateURL(
        selectedMonth.value,
        selectedYear.value,
        selectedWeek.value,
        selectedWeekYear.value,
      );
      loadWeeklyData();
    };

    const goToCurrentMonth = () => {
      const now = new Date();
      selectedMonth.value = now.getMonth() + 1;
      selectedYear.value = now.getFullYear();

      if (viewMode.value === "week") {
        selectedWeek.value = getWeekNumber(now);
        selectedWeekYear.value = now.getFullYear();
      }

      updateURL(
        selectedMonth.value,
        selectedYear.value,
        selectedWeek.value,
        selectedWeekYear.value,
      );
      loadWeeklyData();
    };

    const changeMonth = () => {
      loadWeeklyData();
    };

    const changeMonthFromPicker = () => {
      loadWeeklyData();
      monthPickerOpen.value = false;
    };

    const applyFilters = () => {
      // Filters are applied automatically via computed properties
    };

    const loadMonthlyData = async () => {
      try {
        if (
          !dayReportsStore ||
          typeof dayReportsStore.getMonthlyReports !== "function"
        ) {
          throw new Error("Day reports store is not properly initialized");
        }
        await dayReportsStore.getMonthlyReports(
          selectedMonth.value,
          selectedYear.value,
        );
      } catch (error) {
        console.error("Error loading monthly data:", error);
        // Set error state in the store if it exists
        if (dayReportsStore && typeof dayReportsStore === "object") {
          dayReportsStore.error =
            error.message || "Failed to load monthly reports";
        }
      }
    };

    const loadWeeklyData = async () => {
      try {
        if (
          !dayReportsStore ||
          typeof dayReportsStore.getMonthlyReports !== "function"
        ) {
          throw new Error("Day reports store is not properly initialized");
        }

        if (viewMode.value === "week") {
          // For week view, we need to handle cross-month weeks
          const weekStart = getDateFromWeek(
            selectedWeekYear.value,
            selectedWeek.value,
          );
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);

          const startMonth = weekStart.getMonth() + 1;
          const startYear = weekStart.getFullYear();
          const endMonth = weekEnd.getMonth() + 1;
          const endYear = weekEnd.getFullYear();

          // Check if we already have cached data for these months
          const needsStartMonth = !(
            dayReportsStore.currentMonth === startMonth &&
            dayReportsStore.currentYear === startYear &&
            dayReportsStore.monthlyReports.length > 0
          );
          const needsEndMonth =
            (startMonth !== endMonth || startYear !== endYear) &&
            !(
              dayReportsStore.currentMonth === endMonth &&
              dayReportsStore.currentYear === endYear &&
              dayReportsStore.monthlyReports.length > 0
            );

          // Only fetch data that we don't already have
          const promises = [];
          if (needsStartMonth) {
            promises.push(
              dayReportsStore.getMonthlyReports(startMonth, startYear),
            );
          }
          if (needsEndMonth) {
            promises.push(dayReportsStore.getMonthlyReports(endMonth, endYear));
          }

          if (promises.length > 0) {
            await Promise.all(promises);
          } else {
            console.log(
              `Planner: Using cached data for week view (${startMonth}/${startYear}${needsEndMonth ? ` and ${endMonth}/${endYear}` : ""})`,
            );
          }
        } else {
          // For month view, check if we already have the data
          const needsData = !(
            dayReportsStore.currentMonth === selectedMonth.value &&
            dayReportsStore.currentYear === selectedYear.value &&
            dayReportsStore.monthlyReports.length > 0
          );

          if (needsData) {
            await loadMonthlyData();
          } else {
            console.log(
              `Planner: Using cached monthly data for ${selectedMonth.value}/${selectedYear.value} (${dayReportsStore.monthlyReports.length} reports)`,
            );
          }
        }
      } catch (error) {
        console.error("Error loading weekly data:", error);
        if (dayReportsStore && typeof dayReportsStore === "object") {
          dayReportsStore.error =
            error.message || "Failed to load weekly reports";
        }
      }
    };

    const refreshData = async () => {
      // Force a fresh fetch by temporarily clearing the cache state
      const originalMonth = dayReportsStore.currentMonth;
      const originalYear = dayReportsStore.currentYear;

      // Clear cache state to force refetch
      dayReportsStore.currentMonth = null;
      dayReportsStore.currentYear = null;

      try {
        await loadWeeklyData();
      } catch (error) {
        // Restore original state if refresh fails
        dayReportsStore.currentMonth = originalMonth;
        dayReportsStore.currentYear = originalYear;
        throw error;
      }
    };

    const switchView = (newViewMode) => {
      if (newViewMode === "week" && viewMode.value === "month") {
        // Switching from month to week - find the first week of the month
        const firstOfMonth = new Date(
          selectedYear.value,
          selectedMonth.value - 1,
          1,
        );
        selectedWeek.value = getWeekNumber(firstOfMonth);
        selectedWeekYear.value = firstOfMonth.getFullYear();
      } else if (newViewMode === "month" && viewMode.value === "week") {
        // Switching from week to month - use the month that contains the week
        const weekStart = getDateFromWeek(
          selectedWeekYear.value,
          selectedWeek.value,
        );
        selectedMonth.value = weekStart.getMonth() + 1;
        selectedYear.value = weekStart.getFullYear();
      }

      updateURL(
        selectedMonth.value,
        selectedYear.value,
        selectedWeek.value,
        selectedWeekYear.value,
      );
      loadWeeklyData();
    };

    const createDayReport = () => {
      // Use the existing forms system to create a new day report
      const today = new Date();
      const dateString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      // Use filter values as defaults if only one option is selected
      const defaultUserId =
        selectedUsers.value?.length === 1
          ? selectedUsers.value[0]
          : currentUser.value?.can_plan_day_reports
            ? null
            : currentUser.value?.id;

      const defaultDayReportTypeId =
        selectedDayReportTypes.value?.length === 1
          ? selectedDayReportTypes.value[0]
          : null;

      // Determine if this should be a planned working day report
      let defaultStatus = null;
      if (defaultDayReportTypeId) {
        const selectedType = dayReportTypes.value.find(
          (type) => type.id === defaultDayReportTypeId,
        );
        if (selectedType?.working) {
          defaultStatus = "planned";
        }
      }

      const reportData = {
        date: dateString,
        day_report_type_id: defaultDayReportTypeId,
        user_id: defaultUserId,
        ...(defaultStatus && { day_report_status: defaultStatus }),
      };

      formsStore.edit({
        type: "day_report",
        item: reportData,
        options: { plan_mode: true },
      });
    };

    const createDayReportForDate = (date) => {
      // Use the existing forms system to create a new day report for a specific date

      // Use filter values as defaults if only one option is selected
      const defaultUserId =
        selectedUsers.value?.length === 1
          ? selectedUsers.value[0]
          : currentUser.value?.can_plan_day_reports
            ? null
            : currentUser.value?.id;

      const defaultDayReportTypeId =
        selectedDayReportTypes.value?.length === 1
          ? selectedDayReportTypes.value[0]
          : null;

      // Determine if this should be a planned working day report
      let defaultStatus = null;
      if (defaultDayReportTypeId) {
        const selectedType = dayReportTypes.value.find(
          (type) => type.id === defaultDayReportTypeId,
        );
        if (selectedType?.working) {
          defaultStatus = "planned";
        }
      }

      const reportData = {
        date: date,
        day_report_type_id: defaultDayReportTypeId,
        user_id: defaultUserId,
        ...(defaultStatus && { day_report_status: defaultStatus }),
      };

      formsStore.edit({
        type: "day_report",
        item: reportData,
        options: { plan_mode: true },
      });
    };

    const editDayReport = (report) => {
      formsStore.edit({
        type: "day_report",
        item: report,
        options: { plan_mode: true },
      });
    };

    const viewDayReportDetail = (report) => {
      router.push(`/day_reports/${report.id}`);
    };

    const deleteDayReport = async (report) => {
      if (
        confirm(
          `Weet je zeker dat je het rapport "${report.day_report_type}" van ${report.date} wilt verwijderen?`,
        )
      ) {
        try {
          if (
            !dayReportsStore ||
            typeof dayReportsStore.destroy !== "function"
          ) {
            throw new Error("Day reports store is not properly initialized");
          }
          await dayReportsStore.destroy(report);

          // Refresh current user to update active_day_report_id
          // If this was the active day_report, it will now be null
          await authStore.getCurrentUser(true);
        } catch (error) {
          console.error("Error deleting day report:", error);
        }
      }
    };

    // Hours summary methods
    const toggleHoursSummary = () => {
      showHoursSummary.value = !showHoursSummary.value;
    };

    const toggleUserExpansion = (userId) => {
      if (expandedUsers.value.has(userId)) {
        expandedUsers.value.delete(userId);
      } else {
        expandedUsers.value.add(userId);
      }
    };

    // Lifecycle
    onMounted(async () => {
      try {
        // First, try to load day reports data immediately (might use cache)
        // This will show data fast if we already have it
        loadWeeklyData().catch((err) => {
          console.error("Error loading initial planner data:", err);
        });

        // Then load supporting data in parallel (these have their own caching)
        const promises = [
          dayReportTypesStore.getAll().catch((err) => {
            console.error("Error loading day report types:", err);
            return [];
          }),
          authStore.getCurrentUser().catch((err) => {
            console.error("Error loading current user:", err);
            return null;
          }),
          usersStore.getAll().catch((err) => {
            console.error("Error loading users:", err);
            return [];
          }),
        ];

        await Promise.allSettled(promises);
      } catch (error) {
        console.error("Error during planner initialization:", error);
        // Set error state if needed
        dayReportsStore.error = error.message || "Failed to initialize planner";
      }
    });

    return {
      // Data
      selectedMonth,
      selectedYear,
      selectedWeek,
      selectedWeekYear,
      selectedUsers,
      selectedDayReportTypes,
      monthPickerOpen,
      monthOptions,
      viewMode,
      showHoursSummary,
      expandedUsers,

      // Computed
      loading,
      error,
      currentUser,
      users,
      dayReportTypes,
      monthYearLabel,
      filteredReports,
      allDaysInMonth,
      allDaysInWeek,
      hoursSummaryData,
      usersWithHours,
      usersWithoutHours,
      totalSummary,

      // Methods
      isToday,
      getReportsForDate,
      formatDateHeader,
      formatTimeRange,
      getStatusColor,
      getStatusIconColor,
      getStatusIcon,
      getStatusChipColor,
      canViewDetail,
      previousMonth,
      nextMonth,
      goToCurrentMonth,
      changeMonth,
      changeMonthFromPicker,
      applyFilters,
      loadMonthlyData,
      loadWeeklyData,
      refreshData,
      switchView,
      getWeekNumber,
      getDateFromWeek,
      createDayReport,
      createDayReportForDate,
      editDayReport,
      viewDayReportDetail,
      deleteDayReport,
      parseDurationToHours,
      formatDurationHHMM,
      secondsToHours,
      toggleHoursSummary,
      toggleUserExpansion,
    };
  },
};
</script>

<style scoped>
.border-b {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.v-list-item {
  min-height: 64px;
}

.v-card-title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

/* Week view styles */
.week-view {
  min-height: 400px;
}

.week-column {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
}

.week-column:last-child {
  border-right: none;
}

.weekend-column {
  background-color: rgba(96, 125, 139, 0.02);
}

.day-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.reports-column {
  flex: 1;
  background-color: rgba(0, 0, 0, 0.01);
}

/* Custom width for 7 equal columns */
@media (min-width: 1024px) {
  .week-column {
    flex: 0 0 14.285714%;
    max-width: 14.285714%;
  }
}

/* Today highlighting */
.today-header {
  background-color: rgba(33, 150, 243, 0.2) !important;
  border: 2px solid #2196f3 !important;
  color: #1976d2 !important;
  font-weight: bold !important;
}

/* Hours summary sticky positioning */
.hours-summary-sticky {
  position: sticky;
  top: 80px; /* Account for navigation bar height */
  height: fit-content;
  z-index: 10;
}

.hours-summary-card {
  max-height: calc(100vh - 140px);
  overflow-y: auto;
}

/* Responsive adjustments */
@media (max-width: 1023px) {
  .week-view .v-col {
    min-height: 250px;
  }

  /* Stack hours summary below main content on mobile */
  .hours-summary-sticky {
    position: relative;
    top: 0;
    margin-top: 20px;
  }

  .hours-summary-card {
    max-height: none;
  }
}
</style>
